import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSharedChannel } from "@/lib/useSharedChannel";

/**
 * "Live trip" indicator for the navbar / courier nav.
 *
 * One cached result shared by every consumer, and a single bookings query for
 * all active trips instead of one query per trip.
 */
export const useActiveTrips = () => {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["active-trips", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
    queryFn: async () => {
      const { data: trips, error } = await supabase
        .from("courier_trips")
        .select("id")
        .eq("courier_id", user!.id)
        .in("status", ["active", "in_transit"]);
      if (error) throw error;
      const ids = (trips || []).map((t) => t.id);
      if (ids.length === 0) return { activeTripId: null as string | null };

      const { data: bookings } = await supabase
        .from("bookings")
        .select("trip_id")
        .in("trip_id", ids)
        .not("status", "in", '("delivered","cancelled")');

      const live = new Set((bookings || []).map((b) => b.trip_id));
      const activeTripId = ids.find((id) => live.has(id)) ?? null;
      return { activeTripId };
    },
  });

  useSharedChannel(
    user?.id ? "active-trips" : null,
    user?.id ? { kind: "user", id: user.id } : null,
    [
      { table: "courier_trips", filter: `courier_id=eq.${user?.id}` },
      { table: "bookings", event: "UPDATE" },
    ],
    () => qc.invalidateQueries({ queryKey: ["active-trips", user?.id] }),
  );

  const activeTripId = data?.activeTripId ?? null;

  return {
    activeTripsCount: activeTripId ? 1 : 0,
    activeTripId,
    loading: isLoading,
    refetch,
  };
};
